import { Injectable, signal } from '@angular/core';
import { SITE_LOCALES, type SiteLocale } from '@models/site-locales';

/**
 * NOTE: this is the legacy admin's "country" switcher, which is really a locale filter — it scopes the
 * Reservations and Customers lists to records whose Site has this locale. It does NOT change the UI
 * language, and it does not affect the Lakes/Waters list.
 *
 * The "no filter" option: an empty locale clears the site scope so all records show. Both list
 * services skip the `locale` query param when it is empty, and the API only filters on a non-empty
 * locale.
 */
const ALL_LOCALES: SiteLocale = { code: '', country: 'All countries', flag: 'icons/flags/eu.svg' };
const SWITCHER_OPTIONS: readonly SiteLocale[] = [ALL_LOCALES, ...SITE_LOCALES];

const DEFAULT_LOCALE = ALL_LOCALES.code;
const STORAGE_KEY = 'admin-site-locale';

/**
 * Holds the active site-locale filter, persisted across sessions in localStorage. List components
 * read {@link selectedLocaleCode} inside their request params so switching auto-refetches. An empty
 * string means "all countries" (no filter).
 */
@Injectable({ providedIn: 'root' })
export class LocaleSwitcherService {
  readonly locales = SWITCHER_OPTIONS;

  readonly selectedLocaleCode = signal<string>(this.loadInitial());

  setLocale(code: string): void {
    if (!SWITCHER_OPTIONS.some((l) => l.code === code)) return;
    this.selectedLocaleCode.set(code);
    try {
      localStorage.setItem(STORAGE_KEY, code);
    } catch {
      console.warn('LocaleSwitcherService: could not persist locale selection to localStorage.');
    }
  }

  /** The full {@link SiteLocale} for the current selection (for rendering flag + name). */
  current(): SiteLocale {
    const code = this.selectedLocaleCode();
    return SWITCHER_OPTIONS.find((l) => l.code === code) ?? ALL_LOCALES;
  }

  private loadInitial(): string {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      // getItem returns null when unset; a stored "" (all countries) is a valid selection.
      if (stored !== null && SWITCHER_OPTIONS.some((l) => l.code === stored)) return stored;
    } catch {
      console.warn('LocaleSwitcherService: could not read locale selection from localStorage.');
    }
    return DEFAULT_LOCALE;
  }
}
